// File: frontend/src/services/apiService.js
// Version: 1.20
// Date: 2025-12-18 14:50 EST
// Author: Alexander Fitenko (elfny.com) for NYgot LLC (Nygot.com)
// Description: Centralized API service with robust user fetching.
// History:
// - [v1.20]: Applied Code Style Policy Header
// - [v1.19]: Updated getUser to fallback to list search if direct fetch fails.
// - [v1.18]: Added getUser(userId).
// - [v1.17]: Added deleteCase(caseId).

const API_BASE_URL = '/api/v1';

class ApiService {
    async makeRequest(endpoint, options = {}, isPublic = false) {
        const url = `${API_BASE_URL}${endpoint}`;

        const headers = {
            'Content-Type': 'application/json',
            ...options.headers,
        };

        if (options.body instanceof FormData) {
            delete headers['Content-Type'];
        }

        // We no longer set 'Authorization' header manually. 
        // The HttpOnly cookie 'access_token' will be sent automatically
        // thanks to `credentials: 'include'`.

        const config = { ...options, headers, credentials: 'include' };

        try {
            const response = await fetch(url, config);

            if (response.status === 401 && !isPublic) {
                if (endpoint === '/users/me') {
                    throw new Error('Not authenticated');
                }
                this.logout();
                window.location.href = '/login';
                throw new Error('Session expired. Please log in again.');
            }

            if (!response.ok) {
                const errorText = await response.text();
                let errorData;
                try {
                    errorData = JSON.parse(errorText);
                } catch (e) {
                    errorData = { detail: errorText || 'Request failed with status ' + response.status };
                }
                // 🔴 КОД ОТВЕТА СОХРАНЯЕТСЯ НА ОШИБКЕ. Без него вызывающий не
                // может отличить «не положено» (403) от «не смогли спросить»
                // (500/сеть), и обе беды пришлось бы показывать одними словами
                // либо угадывать по тексту сообщения. Признак решения обязан
                // быть тем, что решение и означает, а не похожей строкой.
                const oshibka = new Error(errorData.detail || `HTTP error ${response.status}`);
                oshibka.status = response.status;
                throw oshibka;
            }

            if (response.status === 204) {
                return null;
            }

            return await response.json();
        } catch (error) {
            console.error(`API Request Error: ${endpoint}`, error.message);
            throw error;
        }
    }

    async login(email, password) {
        const params = new URLSearchParams();
        params.append('username', email);
        params.append('password', password);

        const data = await this.makeRequest('/auth/login/access-token', {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: params,
        }, true);

        // JWT is now set securely via HttpOnly cookie by the backend.
        // No need to store it in localStorage.
        return data;
    }

    logout() {
        // Backend handles cookie deletion, or we just rely on /logout API (if exists)
        // For now, we clear any potential client-side state
        this.makeRequest('/auth/logout', { method: 'POST' }, true).catch(() => {});
    }

    async getMe() {
        return this.makeRequest('/users/me');
    }

    // Checking auth synchronously is no longer reliable with HttpOnly cookies.
    // The app should rely on the `getMe` result.
    isAuthenticated() {
        // Look for any hint of login, but real check is purely server-side
        return document.cookie.includes('access_token');
    }

    async getGoogleAuthUrl() {
        return this.makeRequest('/auth/google/url');
    }

    async disconnectGoogleCalendar() {
        return this.makeRequest('/users/me/calendar/disconnect', {
            method: 'POST',
        });
    }

    async updateMyPersona(ai_persona) {
        return this.makeRequest('/users/me/persona', {
            method: 'PATCH',
            body: JSON.stringify({ ai_persona }),
        });
    }

    // --- 3. МЕТОДЫ ДЛЯ DASHBOARD ---
    async getDashboardData() {
        return this.makeRequest('/dashboard/');
    }

    // --- 4. МЕТОДЫ ДЛЯ УПРАВЛЕНИЯ ЛИДАМИ ---
    async handlePublicChatMessage(chatData) {
        return this.makeRequest('/public/chat', {
            method: 'POST',
            body: JSON.stringify(chatData),
        }, true);
    }

    async publicChatWithAI(chatData) {
        return this.makeRequest('/public/chat/continue', {
            method: 'POST',
            body: JSON.stringify(chatData),
        }, true);
    }

    // 🔴 ОТВЕТ ВОЗВРАЩАЕТСЯ ЦЕЛИКОМ, а не распакованный до массива. Форма
    //    ответа — `{items, vsego, pokazano}` (quick-260911-27v, Д-07):
    //    знаменатель «показаны N из M» живёт в `vsego` и в длину страницы не
    //    помещается по построению.
    //    Второй двери (`getLeadsSvodka`) НЕТ НАРОЧНО: два метода к одной ручке —
    //    два держателя одного договора, и разошлись бы они молча.
    async getLeads(skip = 0, limit = 100) {
        const dovody = new URLSearchParams({ skip: String(skip), limit: String(limit) });
        return this.makeRequest(`/leads/?${dovody.toString()}`);
    }

    async createLead(leadData) {
        return this.makeRequest('/leads/', {
            method: 'POST',
            body: JSON.stringify(leadData),
        }, true);
    }

    async convertLead(leadId) {
        return this.makeRequest(`/leads/${leadId}/convert`, {
            method: 'POST',
        });
    }

    async getLead(leadId) {
        return this.makeRequest(`/leads/${leadId}`);
    }

    async updateLead(leadId, leadData) {
        return this.makeRequest(`/leads/${leadId}`, {
            method: 'PUT',
            body: JSON.stringify(leadData),
        });
    }

    async deleteLead(leadId) {
        return this.makeRequest(`/leads/${leadId}`, {
            method: 'DELETE',
        });
    }

    // --- 4.1. МЕТОДЫ ДЛЯ EMAIL TRIAGE (Email Inbox) ---
    async getPendingEmails() {
        return this.makeRequest('/email-triage/pending');
    }

    async approveEmail(id, reply) {
        return this.makeRequest(`/email-triage/${id}/approve`, {
            method: 'POST',
            body: JSON.stringify({ reply }),
        });
    }

    async dismissEmail(id) {
        return this.makeRequest(`/email-triage/${id}/dismiss`, {
            method: 'POST',
        });
    }

    async syncChatMessages(email, lastMessageId = 0) {
        return this.makeRequest('/public/chat/sync', {
            method: 'POST',
            body: JSON.stringify({ email, last_message_id: lastMessageId }),
        }, true); // isPublic=true
    }

    // --- 4.5. Monitor API (Human Takeover) ---
    async getActiveChats(minutes = 60) {
        return this.makeRequest(`/monitor/active-chats?minutes=${minutes}`);
    }

    async takeoverChat(leadId) {
        return this.makeRequest(`/monitor/chat/${leadId}/takeover`, { method: 'POST' });
    }

    async releaseChat(leadId) {
        return this.makeRequest(`/monitor/chat/${leadId}/release`, { method: 'POST' });
    }

    async sendOperatorMessage(leadId, text) {
        return this.makeRequest(`/monitor/chat/${leadId}/message`, {
            method: 'POST',
            body: JSON.stringify({ text })
        });
    }

    async getVoiceAnalytics(days = 7, profileId = null, showTest = false) {
        let url = `/monitor/analytics/voice?days=${days}`;
        if (profileId && profileId !== 'default') {
            url += `&profile_id=${encodeURIComponent(profileId)}`;
        }
        if (showTest) {
            url += '&show_test=true';
        }
        return this.makeRequest(url);
    }

    // --- 5. МЕТОДЫ ДЛЯ УПРАВЛЕНИЯ ПОЛЬЗОВАТЕЛЯМИ ---

    // === [ИСПРАВЛЕНИЕ v1.19] Умный метод получения пользователя ===
    async getUser(userId) {
        try {
            // 1. Сначала пробуем прямой запрос (правильный способ)
            return await this.makeRequest(`/users/${userId}`);
        } catch (error) {
            // 2. Если сервер ответил "Нельзя" (405) или "Нет такого" (404)
            if (error.message.includes('405') || error.message.includes('Method Not Allowed') || error.message.includes('404')) {
                console.warn(`Direct fetch failed for ID ${userId}. Falling back to list search...`);

                // а) Ищем среди клиентов (там больше деталей, например дела)
                const clients = await this.getClientsWithDetails();
                const foundClient = clients.find(u => String(u.id) === String(userId));
                if (foundClient) return foundClient;

                // б) Если не клиент, ищем среди всех пользователей (сотрудники)
                const allUsers = await this.getUsers();
                const foundUser = allUsers.find(u => String(u.id) === String(userId));
                if (foundUser) return foundUser;
            }
            // Если так и не нашли или другая ошибка — пробрасываем её дальше
            throw error;
        }
    }
    // =========================================================

    async getUsers() {
        return this.makeRequest('/users/');
    }

    async getUsersByRole(roleName) {
        return this.makeRequest(`/users/by-role/${roleName}`);
    }

    async getClientsWithDetails() {
        return this.makeRequest('/users/clients-with-details');
    }

    // История человека: одна лента, два входа. Обе ручки закрыты рубежом
    // сотрудника на бэкенде — клиентская учётка получит 403, а не пустоту.
    getClientHistory(userId) {
        return this.makeRequest(`/history/clients/${userId}`);
    }

    getLeadHistory(leadId) {
        return this.makeRequest(`/history/leads/${leadId}`);
    }

    // 🔴 ЛЕНТА ВСЕЙ ФИРМЫ. Третий вход, и он ОТЛИЧАЕТСЯ от двух прежних тем, что
    // не наследует доступ от предмета: предмета у неё нет. Рубеж на сервере —
    // роль сотрудника (`deps.get_current_staff_user`, тот же, каким закрыт
    // сводный отчёт по всему докету фирмы). Клиентская учётка получает 403, а
    // НЕ пустой список: пустой сказал бы «событий нет».
    getFirmHistory(showTest = false) {
        const query = showTest ? '?show_test=true' : '';
        return this.makeRequest(`/history/firm${query}`);
    }

    // 🔴 ЦИТАТА ИСТОЧНИКА ОДНОГО СОБЫТИЯ — ПО ТРЕБОВАНИЮ, а не в ленте. Лента
    // тела переписки не содержит НИКОГДА; здесь оно спрашивается отдельным
    // обращением и только когда человек раскрыл карточку.
    // 🔴 Ответов у ручки несколько, и они РАЗНЫЕ: 403 (нет права), 404 (события
    // нет), 410 (строка-источник утрачена), 200 с `est_istochnik: false` и
    // причиной, 200 с цитатой. Коды сюда доезжают полем `status` ошибки
    // (`makeRequest` его проставляет) — глушить их общим catch нельзя, иначе на
    // экране «отказ» станет неотличим от «пусто».
    getEventSource(eventId) {
        return this.makeRequest(`/history/events/${eventId}/source`);
    }


    async addUser(userData) {
        return this.makeRequest('/users/', {
            method: 'POST',
            body: JSON.stringify(userData),
        });
    }

    async deleteUser(userId) {
        return this.makeRequest(`/users/${userId}`, {
            method: 'DELETE',
        });
    }

    async updateUser(userId, userData) {
        return this.makeRequest(`/users/${userId}`, {
            method: 'PATCH',
            body: JSON.stringify(userData),
        });
    }

    async adminResetPassword(userId, newPassword) {
        return this.makeRequest(`/users/${userId}/reset-password`, {
            method: 'POST',
            body: JSON.stringify({
                new_password: newPassword,
            }),
        });
    }

    // --- 6. МЕТОДЫ ДЛЯ УПРАВЛЕНИЯ ДЕЛАМИ (CASES) ---
    async getCases() {
        return this.makeRequest('/cases/');
    }

    async createCase(caseData) {
        return this.makeRequest('/cases/', {
            method: 'POST',
            body: JSON.stringify(caseData),
        });
    }

    async deleteCase(caseId) {
        return this.makeRequest(`/cases/${caseId}`, {
            method: 'DELETE',
        });
    }

    async updateCaseStatus(caseId, newStatus) {
        return this.makeRequest(`/cases/${caseId}/status`, {
            method: 'PATCH',
            body: JSON.stringify({ status: newStatus }),
        });
    }

    async searchCases(params) {
        const cleanParams = Object.entries(params).reduce((acc, [key, value]) => {
            if (value !== null && value !== undefined && value !== '') acc[key] = value;
            return acc;
        }, {});
        const query = new URLSearchParams(cleanParams).toString();
        return this.makeRequest(`/cases/search?${query}`);
    }

    async getCaseById(caseId) {
        return this.makeRequest(`/cases/${caseId}`);
    }

    // ── Верхняя панель (quick-260911-dzd) ────────────────────────────────
    // 🔴 СЛУЖБА ВОЗИТ, А НЕ РЕШАЕТ. Ни одного вычисления здесь нет: ни вида
    //    адреса, ни перечня охваченного поиском. Оба приходят с сервера, где
    //    у них ОДИН держатель; посчитать их второй раз здесь значило бы
    //    завести второе мнение, которое разойдётся с первым молча.
    async getOfisy() {
        return this.makeRequest('/ofisy/');
    }

    // ── Экран Today арендатора (Phase 9) — служба возит, а не решает ──
    // Ни одна из ручек не толкуется здесь: пустоту, отказ и сбой различает экран.
    async getTodayCalendarStatus() {
        return this.makeRequest('/today/calendar-status');
    }

    async getTodayInquiries(hours = 24) {
        return this.makeRequest(`/today/inquiries?hours=${hours}`);
    }

    async getTodayBookings(limit = 20) {
        return this.makeRequest(`/today/bookings?limit=${limit}`);
    }

    async getTodaySofiaActivity(hours = 24, showTest = false) {
        let url = `/today/sofia-activity?hours=${hours}`;
        if (showTest) url += '&show_test=true';
        return this.makeRequest(url);
    }

    async getTodayCallTranscript(callId) {
        return this.makeRequest(`/today/calls/${encodeURIComponent(callId)}/transcript`);
    }

    async getTodayChatSession(id) {
        return this.makeRequest(`/today/chat-sessions/${encodeURIComponent(id)}`);
    }

    async getTodayCallsWithoutInquiry(hours = 24, showTest = false) {
        let url = `/today/calls-without-inquiry?hours=${hours}`;
        if (showTest) url += '&show_test=true';
        return this.makeRequest(url);
    }

    // [PROPOSED — правило формата] снимается вместе с ручкой.
    async getTodayConsultationFormats(leadIds) {
        return this.makeRequest(`/today/consultation-formats?lead_ids=${leadIds.map(Number).join(',')}`);
    }

    async poisk(zapros, predel) {
        const dovody = new URLSearchParams({ q: zapros });
        if (predel) dovody.set('predel', String(predel));
        return this.makeRequest(`/poisk/?${dovody.toString()}`);
    }

    // Срок по делу. Служба ВОЗИТ, а не считает: ни одного вычисления здесь нет.
    // Число оставшихся дней приходит с сервера — там известна зона арендатора,
    // а вычислить его повторно на клиенте значило бы завести второго держателя.
    async getBlizhayshiySrok() {
        return this.makeRequest('/case-deadlines/next');
    }

    async setCaseDeadline(caseId, payload) {
        return this.makeRequest(`/case-deadlines/${caseId}`, {
            method: 'PATCH',
            body: JSON.stringify(payload)
        });
    }

    async assignTeamToCase(caseId, assignmentData) {
        return this.makeRequest(`/cases/${caseId}/assign`, {
            method: 'PUT',
            body: JSON.stringify(assignmentData)
        });
    }

    async getCaseTimeline(caseId, filters = {}) {
        const cleanFilters = Object.entries(filters).reduce((acc, [key, value]) => {
            if (value !== null && value !== undefined && value !== '') acc[key] = value;
            return acc;
        }, {});
        const query = new URLSearchParams(cleanFilters).toString();
        return this.makeRequest(`/cases/${caseId}/timeline?${query}`);
    }


    // --- 7. МЕТОДЫ ДЛЯ ВНУТРЕННЕГО ЧАТА ---
    async getChatSessions() {
        return this.makeRequest('/chat/sessions');
    }

    async createChatSession(sessionData) {
        return this.makeRequest('/chat/sessions', {
            method: 'POST',
            body: JSON.stringify(sessionData)
        });
    }

    async getChatSession(sessionId) {
        return this.makeRequest(`/chat/${sessionId}`);
    }
    async sendMessage(sessionId, messageContent) {
        return this.makeRequest(`/chat/${sessionId}/messages`, {
            method: 'POST',
            body: JSON.stringify({ content: messageContent }),
        });
    }

    // --- 8. МЕТОДЫ ДЛЯ УПРАВЛЕНИЯ ДОКУМЕНТАМИ ---
    async getDocuments() {
        return this.makeRequest('/documents/');
    }
    async uploadDocument(file) {
        const formData = new FormData();
        formData.append('file', file);
        return this.makeRequest('/documents/upload', {
            method: 'POST',
            body: formData,
        });
    }

    // --- 9. МЕТОДЫ ДЛЯ INTAKE FORM ---
    async getIntakeQuestions() {
        return this.makeRequest('/intake/questions');
    }
    async getIntakeResponses(caseId) {
        return this.makeRequest(`/intake/responses/${caseId}`);
    }
    async submitIntakeForm(submissionData) {
        return this.makeRequest('/intake/responses', {
            method: 'POST',
            body: JSON.stringify(submissionData),
        });
    }
    async getIntakeCompletion(caseId) {
        return this.makeRequest(`/intake/cases/${caseId}/completion`);
    }
    async analyzeCaseFromIntake(caseId) {
        return this.makeRequest(`/intake/cases/${caseId}/analyze`, { method: 'POST' });
    }

    // --- 10. МЕТОДЫ ДЛЯ AI TASKS & FEEDBACK ---
    async getTasks(filters = {}) {
        const cleanFilters = Object.entries(filters).reduce((acc, [key, value]) => {
            if (value !== null && value !== undefined && value !== '') acc[key] = value;
            return acc;
        }, {});
        const query = new URLSearchParams(cleanFilters).toString();
        return this.makeRequest(`/ai-tasks?${query}`);
    }
    async getMyTasks(params = {}) {
        const cleanParams = Object.entries(params).reduce((acc, [key, value]) => {
            if (value !== null && value !== undefined && value !== '') acc[key] = value;
            return acc;
        }, {});
        const query = new URLSearchParams(cleanParams).toString();
        return this.makeRequest(`/ai-tasks/my?${query}`);
    }
    async createTaskFromMessage(taskData) {
        return this.makeRequest('/ai-tasks/from-message', {
            method: 'POST',
            body: JSON.stringify(taskData),
        });
    }
    async submitFeedback(feedbackData) {
        return this.makeRequest('/feedback/tasks', {
            method: 'POST',
            body: JSON.stringify(feedbackData),
        });
    }

    // --- 11. МЕТОДЫ ДЛЯ CALENDAR ---
    async getCalendarEvents(start, end) {
        if (!start || !end) {
            throw new Error("Start and end dates are required to fetch calendar events.");
        }
        const params = new URLSearchParams({ start, end });
        return this.makeRequest(`/calendar/events?${params.toString()}`);
    }

    async getWorkingHours() {
        return this.makeRequest('/calendar/working-hours');
    }
    async updateWorkingHours(hoursData) {
        return this.makeRequest('/calendar/working-hours', {
            method: 'PUT',
            body: JSON.stringify(hoursData),
        });
    }

    // [261005-2fy] Текст галочки SMS-согласия календаря (только eln; у остальных 404 → галочки нет).
    async getSmsConsentText() {
        return this.makeRequest('/leads/sms-consent', {}, true);
    }

    async getFreeSlots(dateStr) {
        // dateStr should be YYYY-MM-DD
        return this.makeRequest(`/calendar/free-slots?date_str=${dateStr}`, {}, true); // isPublic=true
    }

    // --- 13. МЕТОДЫ МОДУЛЯ РЕПУТАЦИИ ---
    async runReputationAudit(auditData) {
        return this.makeRequest('/reputation/audit/run', {
            method: 'POST',
            body: JSON.stringify(auditData)
        });
    }

    async getAuditStatus(taskId) {
        return this.makeRequest(`/reputation/audit/status/${taskId}`);
    }

    async getProfiles() {
        return this.makeRequest('/reputation/profiles');
    }

    async createProfile(data) {
        return this.makeRequest('/reputation/profiles', {
            method: 'POST',
            body: JSON.stringify(data)
        });
    }

    async updateProfile(id, data) {
        return this.makeRequest(`/reputation/profiles/${id}`, {
            method: 'PUT',
            body: JSON.stringify(data)
        });
    }

    async getBattleSessions(profileId) {
        return this.makeRequest(`/reputation/battle/sessions?profile_id=${profileId}`);
    }

    async startBattle(data) {
        return this.makeRequest('/reputation/battle/start', {
            method: 'POST',
            body: JSON.stringify(data)
        });
    }

    async sendBattleReply(sessionId, message) {
        return this.makeRequest(`/reputation/battle/${sessionId}/reply`, {
            method: 'POST',
            body: JSON.stringify({ message })
        });
    }

    async listProfileFixes(profileId) {
        return this.makeRequest(`/reputation/fixes?profile_id=${profileId}`);
    }

    async uploadMedia(file) {
        const fd = new FormData();
        fd.append('file', file);
        return this.makeRequest('/upload/media', { method: 'POST', body: fd });
    }

    async aiRewrite(data) {
        return this.makeRequest('/reputation/ai/rewrite', { method: 'POST', body: JSON.stringify(data) });
    }

    // --- 14. МЕТОДЫ УПРАВЛЕНИЯ ИИ-АГЕНТАМИ (AI STAFF) ---
    async getAgents() {
        return this.makeRequest('/admin/agents');
    }

    
    // --- Agent Memory & Skill Progression ---
    async getAgentMemories(agentId) {
        return this.makeRequest(`/admin/agents/${agentId}/memories`);
    }

    async addAgentMemory(agentId, data) {
        return this.makeRequest(`/admin/agents/${agentId}/memories`, {
            method: 'POST',
            body: JSON.stringify(data)
        });
    }

    async deleteAgentMemory(agentId, memoryId) {
        return this.makeRequest(`/admin/agents/${agentId}/memories/${memoryId}`, {
            method: 'DELETE'
        });
    }

    async getAgentSkills(agentId) {
        return this.makeRequest(`/admin/agents/${agentId}/skills`);
    }

    async getAgentGallery(id) {
        return this.makeRequest(`/admin/agents/${id}/gallery`);
    }

    // --- AI Swarm Social Intranet ---
    async getSocialGroups() {
        return this.makeRequest('/admin/social/groups');
    }
    
    async createSocialGroup(data) {
        return this.makeRequest('/admin/social/groups', {
            method: 'POST',
            body: JSON.stringify(data)
        });
    }

    async joinSocialGroup(groupId, agentId = null) {
        let url = `/admin/social/groups/${groupId}/join`;
        if (agentId) url += `?agent_id=${agentId}`;
        return this.makeRequest(url, { method: 'POST' });
    }

    async getGroupMessages(groupId) {
        return this.makeRequest(`/admin/social/groups/${groupId}/messages`);
    }

    async postGroupMessage(groupId, data) {
        return this.makeRequest(`/admin/social/groups/${groupId}/messages`, {
            method: 'POST',
            body: JSON.stringify(data)
        });
    }

    async getAgentPosts(agentId) {
        return this.makeRequest(`/admin/social/agents/${agentId}/posts`);
    }

    async getAgentSocialGroups(agentId) {
        return this.makeRequest(`/admin/social/agents/${agentId}/groups`);
    }

    // --- Omniscience Engine Additions ---
    async getAgentDMs(agentId) {
        return this.makeRequest(`/admin/social/agents/${agentId}/dms`);
    }

    async clearAgentDMs(agentId) {
        return this.makeRequest(`/admin/social/agents/${agentId}/dms`, {
            method: 'DELETE'
        });
    }

    async deleteAgentDM(agentId, messageId) {
        return this.makeRequest(`/admin/social/agents/${agentId}/dms/${messageId}`, {
            method: 'DELETE'
        });
    }

    async deletePhoto(photoIdOrUrl) {
        const isUrl = typeof photoIdOrUrl === 'string' && (photoIdOrUrl.includes('/') || photoIdOrUrl.includes('.'));
        return this.makeRequest('/admin/social/photos', {
            method: 'DELETE',
            body: JSON.stringify(isUrl ? { url: photoIdOrUrl } : { photo_id: photoIdOrUrl })
        });
    }

    async deleteAgentGalleryPhoto(agentId, photoId) {
        return this.makeRequest(`/admin/agents/${agentId}/gallery/${photoId}`, {
            method: 'DELETE'
        });
    }

    
    async postAgentSelfie(agentId, contextHint = '') {
        return this.makeRequest(`/admin/social/agents/${agentId}/selfie`, {
            method: 'POST',
            body: JSON.stringify({ context: contextHint })
        });
    }

    async updateAgentProfile(agentId, data) {
        return this.makeRequest(`/admin/agents/${agentId}`, {
            method: 'PUT',
            body: JSON.stringify(data)
        });
    }

    async postAgentDM(agentId, data) {
        return this.makeRequest(`/admin/social/agents/${agentId}/dms`, {
            method: 'POST',
            body: JSON.stringify(data)
        });
    }

    async getTaskBoard() {
        return this.makeRequest('/admin/social/tasks');
    }

    async createTaskBoardJob(data) {
        return this.makeRequest('/admin/social/tasks', {
            method: 'POST',
            body: JSON.stringify(data)
        });
    }

    async getOmniscienceNotifications() {
        return this.makeRequest('/admin/social/notifications');
    }

    async updateAgentLocation(agentId, location) {
        return this.makeRequest(`/admin/agents/${agentId}/location`, {
            method: 'PUT',
            body: JSON.stringify({ location })
        });
    }

    async testAgentSandbox(id, messages) {
        return this.makeRequest(`/admin/agents/${id}/sandbox`, {
            method: 'POST',
            body: JSON.stringify({ messages })
        });
    }

    async createAgent(data) {
        return this.makeRequest('/admin/agents', {
            method: 'POST',
            body: JSON.stringify(data)
        });
    }

    async updateAgent(id, data) {
        return this.makeRequest(`/admin/agents/${id}`, {
            method: 'PUT',
            body: JSON.stringify(data)
        });
    }

    async deleteAgent(id) {
        return this.makeRequest(`/admin/agents/${id}`, {
            method: 'DELETE'
        });
    }

    // --- 15. MAGIC EXECUTOR ОРКЕСТРАТОР ---
    async executeMagicUrl(payload) {
        return this.makeRequest('/organic-seo/execute_url', {
            method: 'POST',
            body: JSON.stringify(payload)
        });
    }

    // --- [Phase 06.1] Instance Profile (Creator-only) ---
    // Calls admin API created in Plan 06.1-01. Requires Creator role
    // (afitbk@gmail.com or user.roles contains {name: 'CREATOR'}).
    async getInstanceProfile() {
        return this.makeRequest('/admin/instance-profile');
    }

    async updateInstanceProfile(data) {
        return this.makeRequest('/admin/instance-profile', {
            method: 'PUT',
            body: JSON.stringify(data),
        });
    }

    // --- [Phase P2/P3] Firm Profile Practice Areas ---
    async getPracticeAreas() {
        return this.makeRequest('/firm-profile/practice-areas');
    }

    // --- МЕТОДЫ ДЛЯ КОНВЕЙЕРА БЛОГА (Часть A) ---
    async getBlogOverview() {
        return this.makeRequest('/blog-pipeline/overview');
    }

    async getBlogSuggestions(status) {
        const query = status && status !== 'all' ? `?status=${encodeURIComponent(status)}` : '';
        return this.makeRequest(`/blog-pipeline/suggestions${query}`);
    }

    async createBlogSuggestion(data) {
        return this.makeRequest('/blog-pipeline/suggestions', {
            method: 'POST',
            body: JSON.stringify(data),
        });
    }

    async updateBlogSuggestion(id, data) {
        return this.makeRequest(`/blog-pipeline/suggestions/${id}`, {
            method: 'PATCH',
            body: JSON.stringify(data),
        });
    }

    async deleteBlogSuggestion(id) {
        return this.makeRequest(`/blog-pipeline/suggestions/${id}`, {
            method: 'DELETE',
        });
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// РАЗБОР ОТВЕТА СПИСКА ОБРАЩЕНИЙ — ЕДИНСТВЕННЫЙ ДЕРЖАТЕЛЬ ДОГОВОРА.
//
// 🔴 ЗАЧЕМ ОН ВООБЩЕ ПОЯВИЛСЯ. До 11.09.2026 оба потребителя `getLeads()`
//    писали `Array.isArray(r) ? r : []`. Когда ручка сменила форму со списка на
//    объект, эта строка отдала бы ОБОИМ пустой список И ПРОМОЛЧАЛА: у экрана
//    истории тихо исчезли бы имена обращений в подписях ленты, и ни одна
//    проверка этого не заметила бы. Пустой список означает «обращений нет» —
//    а это была бы НЕПРАВДА.
//
// 🔴 СОСТОЯНИЙ ТРИ, И ТРЕТЬЕ — НЕ «ПУСТО».
//      'spisok' + items.length > 0  — есть обращения;
//      'spisok' + items.length === 0 — обращений НЕТ (законная пустота);
//      'otkaz'                       — ОТВЕТ НЕ ТОЙ ФОРМЫ, то есть ОТКАЗ
//                                      ИЗМЕРИТЕЛЯ. Сказать за него «нет» нельзя.
//    [[knowledge_unknown_is_not_no]]
//
// 🔴 ЗНАМЕНАТЕЛЬ БЕРЁТСЯ ТОЛЬКО ИЗ `vsego`. Подстановка `items.length` при
//    отсутствующем `vsego` вернула бы ровно то враньё, ради которого всё и
//    затевалось («100 из 100» при 109 живых), — поэтому её здесь нет, а есть
//    отказ.
export const razobratSpisokObrashcheniy = (otvet) => {
    if (!otvet || typeof otvet !== 'object' || Array.isArray(otvet)) {
        return { rod: 'otkaz', tekst: 'ответ списка обращений не той формы: ожидался объект '
                 + `{items, vsego, pokazano}, пришло ${Array.isArray(otvet) ? 'массив' : typeof otvet}` };
    }
    if (!Array.isArray(otvet.items)) {
        return { rod: 'otkaz', tekst: 'в ответе списка обращений нет поля items' };
    }
    if (typeof otvet.vsego !== 'number') {
        return { rod: 'otkaz', tekst: 'в ответе списка обращений нет числа vsego — '
                 + 'знаменатель подставить нечем, и подставлять его нельзя' };
    }
    return {
        rod: 'spisok',
        items: otvet.items,
        vsego: otvet.vsego,
        pokazano: typeof otvet.pokazano === 'number' ? otvet.pokazano : otvet.items.length,
    };
};

// ─────────────────────────────────────────────────────────────────────────────
// РАЗБОР ОТВЕТА ОФИСОВ — ЕДИНСТВЕННЫЙ ДЕРЖАТЕЛЬ ДОГОВОРА (quick-260911-dzd).
//
// 🔴 СОСТОЯНИЙ ТРИ, И НИ ОДНО НЕ ПОДМЕНЯЕТ ДРУГОЕ:
//      'spisok' + длина > 0  — офисы есть;
//      'spisok' + длина 0    — офисов НЕТ (законная пустота: у mlg и eln файла
//                              канона нет и быть не должно);
//      'otkaz'               — форма ответа не та, то есть ОТКАЗ ИЗМЕРИТЕЛЯ.
//    Слить второе с третьим значило бы сказать «офисов нет» там, где их не
//    смогли прочитать. [[feedback_unknown_is_not_no]]
//
// 🔴 ВИДЫ АДРЕСА НЕ ВЫЧИСЛЯЮТСЯ ЗДЕСЬ. `adres_vnutri` и `adres_naruzhu`
//    приходят с сервера. Собрать их из частей на клиенте значило бы завести
//    ВТОРОЙ держатель правила публичности — и молчаливо выложить наружу улицу
//    филиала, которую публиковать нельзя.
export const razobratOfisy = (otvet) => {
    if (!otvet || typeof otvet !== 'object' || Array.isArray(otvet)) {
        return { rod: 'otkaz', tekst: 'ответ офисов не той формы: ожидался объект '
                 + `{ofisy, vsego}, пришло ${Array.isArray(otvet) ? 'массив' : typeof otvet}` };
    }
    if (!Array.isArray(otvet.ofisy)) {
        return { rod: 'otkaz', tekst: 'в ответе офисов нет поля ofisy' };
    }
    const nepolnye = otvet.ofisy.filter(
        (o) => !o || typeof o.kluch !== 'string' || typeof o.adres_vnutri !== 'string'
               || typeof o.adres_naruzhu !== 'string');
    if (nepolnye.length) {
        return { rod: 'otkaz', tekst: `офисов без ключа или без готового вида адреса: ${nepolnye.length}` };
    }
    return { rod: 'spisok', ofisy: otvet.ofisy, vsego: otvet.ofisy.length };
};

// ─────────────────────────────────────────────────────────────────────────────
// РАЗБОР ОТВЕТА ПОИСКА — ЕДИНСТВЕННЫЙ ДЕРЖАТЕЛЬ ДОГОВОРА (quick-260911-dzd).
//
// 🔴 ПЕРЕЧЕНЬ ОХВАЧЕННОГО И НЕОХВАЧЕННОГО ОБЯЗАН ПРИЙТИ С СЕРВЕРА. Подпись под
//    строкой поиска — это утверждение о том, ЧТО ИМЕННО посмотрели. Набранная
//    во фронте руками, она разошлась бы с настоящим охватом молча, и человек
//    поверил бы «не найдено» там, где просто не искали. Нет перечня в
//    ответе — ОТКАЗ, а не подстановка своего.
export const razobratPoisk = (otvet) => {
    if (!otvet || typeof otvet !== 'object' || Array.isArray(otvet)) {
        return { rod: 'otkaz', tekst: 'ответ поиска не той формы' };
    }
    if (!Array.isArray(otvet.lyudi) || !Array.isArray(otvet.dela)) {
        return { rod: 'otkaz', tekst: 'в ответе поиска нет списков lyudi и dela' };
    }
    if (!Array.isArray(otvet.ohvacheno) || !otvet.ohvacheno.length
        || !Array.isArray(otvet.ne_ohvacheno) || !otvet.ne_ohvacheno.length) {
        return { rod: 'otkaz', tekst: 'ответ поиска не называет, что охвачено, а что нет — '
                 + 'подпись под строкой обещала бы больше, чем сервер смотрит' };
    }
    const usechenie = (otvet.usechenie && typeof otvet.usechenie === 'object')
        ? otvet.usechenie : null;
    if (!usechenie) {
        return { rod: 'otkaz', tekst: 'в ответе поиска нет признака усечения — '
                 + 'показанное выдавалось бы за всё найденное' };
    }
    return {
        rod: 'nahodki',
        zapros: otvet.zapros,
        lyudi: otvet.lyudi,
        dela: otvet.dela,
        usechenie,
        ohvacheno: otvet.ohvacheno,
        neOhvacheno: otvet.ne_ohvacheno,
        vsegoNahodok: otvet.lyudi.length + otvet.dela.length,
    };
};

export default new ApiService();